
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast, showSuccess } from '@/hooks/use-toast';
import { CampaignFormData, Location } from '../types';
import { localDraftService } from '@/services/LocalDraftService';
import { resolveSuggestedLocations } from '@/utils/locationMatching';
import { locations as datasetLocations } from '@/data/locations';
import { devLog, devError } from '@/utils/secureLogger';
import { secureStorage } from '@/utils/secureStorage';
import { supabase } from '@/integrations/supabase/client';
import { useCreativeUpload } from './useCreativeUpload';
import { validateAutoSaveData } from '@/utils/campaignValidation';
import { autoSaveQueue } from '@/utils/autoSaveQueue';
import { DateStorageService } from '@/components/calendar/services/DateStorageService';

export const useSimpleStepWizard = (
  onSubmit: () => void, 
  draftId?: string, 
  campaignId?: string, 
  mode: 'create' | 'draft' | 'edit' = 'create'
) => {
  const { toast } = useToast();
  
  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [campaignName, setCampaignName] = useState('');
  const [formData, setFormData] = useState<CampaignFormData>({
    companyName: '',
    brandName: '',
    personInCharge: '',
    jobTitle: '',
    email: '',
    mobile: ''
  });
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);
  const [duration, setDuration] = useState(14);
  const [devices, setDevices] = useState(0);

  // Integrate real creative upload functionality
  const {
    creatives,
    locationCreatives,
    mediaOwnerCreatives,
    isUploading: isCreativeUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    removeCreative,
    handleAreaClick,
    handleLocationFileChange,
    handleMediaOwnerFileChange,
    removeLocationCreative,
    removeMediaOwnerCreative,
    handleUrlSubmit,
    handleLocationUrlSubmit,
    handleMediaOwnerUrlSubmit,
    updateLocationCreativeDays,
    updateMediaOwnerCreativeDays,
    useLocationSpecificCreatives,
    setUseLocationSpecificCreatives,
    useMediaOwnerSpecificCreatives,
    setUseMediaOwnerSpecificCreatives
  } = useCreativeUpload();

  // Auto-save management
  const isInitialized = useRef(false);
  const isNewCampaign = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const lastSavedDataRef = useRef<string>('');
  const hasLoadedInitialData = useRef(false);
  const aiSuggestionHandledRef = useRef(false);
  const triggerAutoSaveRef = useRef<(() => void) | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'validating' | 'retrying'>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Auto-save function for edit mode - saves to database (including creatives)
  const performEditAutoSave = useCallback(async () => {
    if (mode !== 'edit' || !campaignId) return;
    if (!hasLoadedInitialData.current) return;

    // Collect all creative URLs
    const allCreativeUrls: string[] = [
      ...creatives.map(c => c.url),
      ...Object.values(locationCreatives).flat().map(c => c.url),
      ...Object.values(mediaOwnerCreatives).flat().map(c => c.url)
    ].filter(Boolean);

    const currentData = {
      campaignName,
      formData,
      selectedLocations,
      duration,
      devices,
      creativeUrls: allCreativeUrls
    };

    const currentDataString = JSON.stringify(currentData);
    
    // Skip if no changes
    if (currentDataString === lastSavedDataRef.current) {
      return;
    }

    setAutoSaveStatus('saving');
    devLog('💾 EDIT AUTO-SAVE: Saving changes to database (including creatives)...');

    try {
      const { error } = await supabase
        .from('saved_campaigns')
        .update({
          campaign_name: campaignName,
          company_name: formData.companyName,
          brand_name: formData.brandName,
          person_in_charge: formData.personInCharge,
          job_title: formData.jobTitle,
          email: formData.email,
          mobile: formData.mobile,
          selected_locations: selectedLocations as any,
          devices_count: devices,
          creative_urls: allCreativeUrls,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaignId);

      if (error) {
        devError('Edit auto-save failed:', error);
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } else {
        lastSavedDataRef.current = currentDataString;
        setAutoSaveStatus('saved');
        devLog('💾 EDIT AUTO-SAVE: Changes saved successfully (including creatives)');
        
        // Reset to idle after 2 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }
    } catch (error) {
      devError('Edit auto-save error:', error);
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  }, [mode, campaignId, campaignName, formData, selectedLocations, duration, devices, creatives, locationCreatives, mediaOwnerCreatives]);

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    // Use edit auto-save for edit mode
    if (mode === 'edit') {
      return performEditAutoSave();
    }

    // Original draft auto-save logic for create/draft modes
    // Always require a draftId for saving
    const currentDraftId = draftId || localDraftService.getCurrentDraftId();
    if (!currentDraftId) {
      devLog('Auto-save skipped - no draft ID available');
      return;
    }

    // For existing campaigns, only save after initial data has been loaded
    if (!hasLoadedInitialData.current && !isNewCampaign.current) {
      devLog('Auto-save skipped - waiting for initial data to load');
      return;
    }

    // Create current data snapshot
    const currentData = {
      campaignName: campaignName || 'Untitled Campaign',
      currentStep,
      selectedLocations,
      formData,
      duration,
      devices,
      useLocationSpecificCreatives,
      useMediaOwnerSpecificCreatives
    };

    const currentDataString = JSON.stringify(currentData);
    
    // Skip save if data hasn't changed (but allow first save for new campaigns)
    if (currentDataString === lastSavedDataRef.current && !isNewCampaign.current) {
      devLog('Auto-save skipped - no changes detected');
      return;
    }

    devLog(`💾 AUTO-SAVE: Starting auto-save for draft: ${currentDraftId}`);
    devLog('💾 AUTO-SAVE: Locations being saved:', currentData.selectedLocations.map(loc => ({ name: loc.name, devices: loc.devices })));

    try {
      const existing = localDraftService.getDraft(currentDraftId);
      const draftData = {
        id: currentDraftId,
        name: currentData.campaignName,
        currentStep: currentData.currentStep,
        selectedLocations: currentData.selectedLocations,
        formData: currentData.formData,
        duration: currentData.duration,
        devices: currentData.devices,
        creatives: [],
        creativeUrls: [],
        startDate: '',
        endDate: '',
        budgetCalculations: {
          totalCost: 0,
          costPerDevice: 0,
          costPerDay: 0
        },
        locationCreatives: {},
        mediaOwnerCreatives: {},
        useLocationSpecificCreatives: currentData.useLocationSpecificCreatives,
        useMediaOwnerSpecificCreatives: currentData.useMediaOwnerSpecificCreatives,
        createdAt: existing?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      devLog('💾 AUTO-SAVE: Draft data being saved:', JSON.stringify(draftData, null, 2));
      localDraftService.saveDraft(draftData);
      // Keep localStorage/session in sync for components reading from it
      localDraftService.loadDraftData(currentDraftId);
      devLog(`💾 AUTO-SAVE: Auto-save successful for draft: ${currentDraftId}`);
      devLog('💾 AUTO-SAVE: Saved locations count:', draftData.selectedLocations?.length || 0);
      
      // Update last saved data reference and clear new campaign flag
      lastSavedDataRef.current = currentDataString;
      if (isNewCampaign.current) {
        devLog('First save completed for new campaign, clearing new campaign flag');
        isNewCampaign.current = false;
      }
      
      // Sync to localStorage for compatibility
      localStorage.setItem('currentStep', currentData.currentStep.toString());
      localStorage.setItem('campaignName', currentData.campaignName);
      localStorage.setItem('selectedLocations', JSON.stringify(currentData.selectedLocations));
      localStorage.setItem('formData', JSON.stringify(currentData.formData));
      localStorage.setItem('duration', currentData.duration.toString());
      localStorage.setItem('devices', currentData.devices.toString());
      localStorage.setItem('useLocationSpecificCreatives', currentData.useLocationSpecificCreatives.toString());
      localStorage.setItem('useMediaOwnerSpecificCreatives', currentData.useMediaOwnerSpecificCreatives.toString());
        
    } catch (error) {
      devError(`Auto-save failed:`, error);
    }
  }, [
    draftId, campaignName, currentStep, selectedLocations, 
    formData, duration, devices, useLocationSpecificCreatives, useMediaOwnerSpecificCreatives
  ]);

  // Debounced auto-save
  const triggerAutoSave = useCallback(() => {
    devLog('💾 AUTO-SAVE: Data changed, saving...');
    devLog('💾 AUTO-SAVE: Current locations count:', selectedLocations.length);
    devLog('💾 AUTO-SAVE: Has loaded initial data:', hasLoadedInitialData.current);
    devLog('💾 AUTO-SAVE: Is new campaign:', isNewCampaign.current);
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Use shorter delay for immediate auto-save on changes
    const delay = 500; // 500ms debounce for all modes
    devLog('💾 AUTO-SAVE: Using delay:', delay, 'ms');
    
    saveTimeoutRef.current = setTimeout(() => {
      performAutoSave();
    }, delay);
  }, [performAutoSave, selectedLocations.length, mode]);

  // Trigger auto-save on any data change (including creatives)
  useEffect(() => {
    // Only trigger auto-save if we have meaningful data or it's a new campaign
    const hasData = campaignName || 
                   Object.values(formData).some(value => value.trim() !== '') ||
                   selectedLocations.length > 0 ||
                   currentStep > 0;

    if (hasData || isNewCampaign.current) {
      triggerAutoSave();
    }
  }, [
    campaignName, currentStep, selectedLocations, formData, duration, 
    devices, useLocationSpecificCreatives, useMediaOwnerSpecificCreatives, 
    creatives, locationCreatives, mediaOwnerCreatives, triggerAutoSave
  ]);

  // Load draft data on mount
  useEffect(() => {
    const loadDraftData = async () => {
      setIsLoading(true);
      
      try {
        // Handle edit mode - fetch campaign from saved_campaigns
        if (mode === 'edit' && campaignId) {
          devLog('Loading campaign for editing:', campaignId);
          
          const { data: campaign, error } = await supabase
            .from('saved_campaigns')
            .select('*')
            .eq('id', campaignId)
            .single();
          
          if (error) {
            devError('Error fetching campaign:', error);
            toast({
              title: "Error",
              description: "Failed to load campaign data",
              variant: "destructive"
            });
          } else if (campaign) {
            devLog('Campaign loaded:', campaign);
            
            // Pre-fill form with campaign data
            setCampaignName(campaign.campaign_name || '');
            setFormData({
              companyName: campaign.company_name || '',
              brandName: campaign.brand_name || '',
              personInCharge: campaign.person_in_charge || '',
              jobTitle: campaign.job_title || '',
              email: campaign.email || '',
              mobile: campaign.mobile || ''
            });
            
            // Load selected locations
            const locations = campaign.selected_locations as any[] || [];
            setSelectedLocations(locations);
            setDevices(campaign.devices_count || 0);
            
            // Calculate duration from dates
            if (campaign.start_date && campaign.end_date) {
              const start = new Date(campaign.start_date);
              const end = new Date(campaign.end_date);
              const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
              setDuration(days);
            }
            
            // Load creative URLs from database
            if (campaign.creative_urls && Array.isArray(campaign.creative_urls)) {
              devLog('🎨 Loading creatives from database:', campaign.creative_urls);
              // Save to localStorage so useCreativeUpload can pick them up
              localStorage.setItem('creativeUrls', JSON.stringify(campaign.creative_urls));
              localStorage.setItem('allCreativeUrls', JSON.stringify(campaign.creative_urls));
            }
            
            hasLoadedInitialData.current = true;
            
            toast({
              title: "Campaign Loaded",
              description: `Editing: ${campaign.campaign_name}`,
            });
          }
        } else if (draftId) {
          // Ensure this draft becomes the current context for saves and other hooks
          localDraftService.setCurrentDraftId(draftId);
          devLog('Loading draft with ID:', draftId);
          const draft = localDraftService.getDraft(draftId);
          // Hydrate full session/localStorage for cross-component consumers
          localDraftService.loadDraftData(draftId);
          
          if (draft) {
            // Existing draft - load all data
            devLog('🔄 LOADING: Found existing draft data, loading...', {
              campaignName: draft.name,
              currentStep: draft.currentStep,
              locationsCount: draft.selectedLocations?.length || 0
            });
            devLog('🔄 LOADING: Draft locations:', draft.selectedLocations?.map(loc => ({ name: loc.name, devices: loc.devices })) || []);
            
            // Load all the data from the draft
            setCurrentStep(draft.currentStep || 0);
            setCampaignName(draft.name || '');
            
            // Ensure locations are properly loaded with full data validation
            const loadedLocations = draft.selectedLocations || [];
            devLog('🔄 LOADING: Loading locations from draft:', loadedLocations);
            setSelectedLocations(loadedLocations);
            
            setFormData(draft.formData || {
              companyName: '',
              brandName: '',
              personInCharge: '',
              jobTitle: '',
              email: '',
              mobile: ''
            });
            setDuration(draft.duration || 14);
            
            // Recalculate devices from loaded locations
            const totalDevices = loadedLocations.reduce((sum: number, loc: any) => sum + (loc.devices || 0), 0);
            setDevices(totalDevices);
            devLog('🔄 LOADING: Recalculated devices from locations:', totalDevices);
            
            setUseLocationSpecificCreatives(draft.useLocationSpecificCreatives || false);
            setUseMediaOwnerSpecificCreatives(draft.useMediaOwnerSpecificCreatives || false);
            
            // Set the last saved data to prevent immediate auto-save for existing drafts
            const loadedData = {
              campaignName: draft.name || '',
              currentStep: draft.currentStep || 0,
              selectedLocations: loadedLocations,
              formData: draft.formData || {
                companyName: '',
                brandName: '',
                personInCharge: '',
                jobTitle: '',
                email: '',
                mobile: ''
              },
              duration: draft.duration || 14,
              devices: totalDevices,
              useLocationSpecificCreatives: draft.useLocationSpecificCreatives || false,
              useMediaOwnerSpecificCreatives: draft.useMediaOwnerSpecificCreatives || false
            };
            lastSavedDataRef.current = JSON.stringify(loadedData);
            hasLoadedInitialData.current = true;
            
            // Sync loaded data to localStorage to ensure consistency
            localStorage.setItem('currentStep', (draft.currentStep || 0).toString());
            localStorage.setItem('campaignName', draft.name || '');
            localStorage.setItem('selectedLocations', JSON.stringify(loadedLocations));
            localStorage.setItem('formData', JSON.stringify(draft.formData || loadedData.formData));
            localStorage.setItem('duration', (draft.duration || 14).toString());
            localStorage.setItem('devices', totalDevices.toString());
            
            // Only show toast if this is the first load of this draft
            if (!sessionStorage.getItem(`simple-draft-loaded-${draftId}`)) {
              toast({
                title: "Draft Loaded",
                description: `Resumed campaign: ${draft.name}`,
              });
              sessionStorage.setItem(`simple-draft-loaded-${draftId}`, 'true');
            }
          } else {
            // No existing draft found with this ID - try to reconstruct from session/localStorage
            devLog('No existing draft found with ID:', draftId, '- attempting reconstruction from session');

            const getJson = (key: string, fallback: any) => {
              try {
                const v = localStorage.getItem(key);
                return v ? JSON.parse(v) : fallback;
              } catch {
                return fallback;
              }
            };
            const getNum = (key: string, fallback: number) => {
              const v = parseInt(localStorage.getItem(key) || '', 10);
              return isNaN(v) ? fallback : v;
            };

            const sessionName = localStorage.getItem('campaignName') || 'Untitled Campaign';
            const loadedLocations = getJson('selectedLocations', []);
            const totalDevices = Array.isArray(loadedLocations)
              ? loadedLocations.reduce((sum: number, loc: any) => sum + (loc?.devices || 0), 0)
              : 0;

            const reconstructed = {
              id: draftId,
              name: sessionName,
              currentStep: getNum('currentStep', 0),
              selectedLocations: loadedLocations,
              formData: getJson('formData', {
                companyName: '',
                brandName: '',
                personInCharge: '',
                jobTitle: '',
                email: '',
                mobile: ''
              }),
              duration: getNum('duration', 14),
              devices: totalDevices,
              creatives: [],
              creativeUrls: [],
              startDate: localStorage.getItem('startDate') || '',
              endDate: localStorage.getItem('endDate') || '',
              budgetCalculations: getJson('budgetCalculations', {}),
              locationCreatives: getJson('locationCreatives', {}),
              mediaOwnerCreatives: getJson('mediaOwnerCreatives', {}),
              useLocationSpecificCreatives: (localStorage.getItem('useLocationSpecificCreatives') === 'true'),
              useMediaOwnerSpecificCreatives: (localStorage.getItem('useMediaOwnerSpecificCreatives') === 'true'),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            devLog('🛠️ Reconstructed draft from session:', reconstructed);
            localDraftService.saveDraft(reconstructed as any);
            localDraftService.loadDraftData(draftId);

            // Hydrate state from reconstructed data
            setCurrentStep(reconstructed.currentStep || 0);
            setCampaignName(reconstructed.name || '');
            setSelectedLocations(loadedLocations);
            setFormData(reconstructed.formData);
            setDuration(reconstructed.duration);
            setDevices(totalDevices);
            setUseLocationSpecificCreatives(reconstructed.useLocationSpecificCreatives);
            setUseMediaOwnerSpecificCreatives(reconstructed.useMediaOwnerSpecificCreatives);

            lastSavedDataRef.current = JSON.stringify({
              campaignName: reconstructed.name,
              currentStep: reconstructed.currentStep,
              selectedLocations: loadedLocations,
              formData: reconstructed.formData,
              duration: reconstructed.duration,
              devices: totalDevices,
              useLocationSpecificCreatives: reconstructed.useLocationSpecificCreatives,
              useMediaOwnerSpecificCreatives: reconstructed.useMediaOwnerSpecificCreatives
            });
            hasLoadedInitialData.current = true;

            toast({
              title: 'Draft Reconstructed',
              description: `Recovered campaign from your session: ${sessionName}`,
            });
          }
        } else if (!draftId) {
          // Creating new campaign - check localStorage first
          devLog('No draft ID provided, loading from localStorage as new campaign');
          isNewCampaign.current = true;
          hasLoadedInitialData.current = true;
          loadFromLocalStorage();
        }
      } catch (error) {
        devError('Error loading draft:', error);
        isNewCampaign.current = true;
        hasLoadedInitialData.current = true;
        loadFromLocalStorage();
      } finally {
        setIsLoading(false);
        // Set initialization flag after a brief delay
        setTimeout(() => {
          isInitialized.current = true;
          devLog('Initialization complete, auto-save fully enabled');
        }, 50);
      }
    };

    const loadFromLocalStorage = () => {
      const savedStep = localStorage.getItem('currentStep');
      const savedName = localStorage.getItem('campaignName');
      const savedFormData = localStorage.getItem('formData');
      const savedLocations = localStorage.getItem('selectedLocations');
      const savedDuration = localStorage.getItem('duration');
      const savedDevices = localStorage.getItem('devices');
      
      // Check for AI suggestion data
      const aiSuggestionData = localStorage.getItem('aiSuggestionData');
      const aiSuggestionApplied = localStorage.getItem('aiSuggestionApplied');
      
      if (aiSuggestionData && aiSuggestionApplied === 'true') {
        try {
          const suggestion = JSON.parse(aiSuggestionData);
          devLog('useSimpleStepWizard: Loading AI suggestion data:', suggestion);
          
          // Apply AI suggestion data with priority over regular localStorage
          if (suggestion.campaignName) {
            setCampaignName(suggestion.campaignName);
          }
          if (suggestion.duration) {
            setDuration(suggestion.duration);
          }
          if (suggestion.companyName || suggestion.brandName || suggestion.personInCharge) {
            const aiFormData = {
              companyName: suggestion.companyName || '',
              brandName: suggestion.brandName || '',
              personInCharge: suggestion.personInCharge || '',
              jobTitle: suggestion.jobTitle || '',
              email: suggestion.email || '',
              mobile: suggestion.mobile || ''
            };
            setFormData(aiFormData);
          }
          if (suggestion.suggestedLocations && suggestion.suggestedLocations.length > 0) {
            devLog('useSimpleStepWizard: Resolving AI suggested locations against dataset:', suggestion.suggestedLocations);
            
            const selectedLocationObjects = resolveSuggestedLocations(suggestion.suggestedLocations, datasetLocations as any);

            setSelectedLocations(selectedLocationObjects as any);
            const totalDevices = selectedLocationObjects.reduce((sum: number, loc: any) => sum + (loc.devices || 0), 0);
            setDevices(totalDevices);
            
            // Also store in localStorage for persistence
            localStorage.setItem('selectedLocations', JSON.stringify(selectedLocationObjects));
            localStorage.setItem('devices', totalDevices.toString());
            
            devLog('useSimpleStepWizard: Applied locations count:', selectedLocationObjects.length);
            devLog('useSimpleStepWizard: Total devices:', totalDevices);
          }

          // Apply dates based on duration using DateStorageService
          if (suggestion.duration) {
            const today = new Date();
            const minAllowedDate = new Date(today);
            minAllowedDate.setDate(today.getDate() + 3);
            minAllowedDate.setHours(0, 0, 0, 0);
            
            const startDate = new Date(minAllowedDate);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + suggestion.duration - 1);
            
            DateStorageService.saveDate(startDate, 'start');
            DateStorageService.saveDate(endDate, 'end');
            DateStorageService.saveDuration(suggestion.duration);
            
            devLog('useSimpleStepWizard: Applied dates for duration:', suggestion.duration);
          }

          // Clear the AI suggestion flags after applying
          localStorage.removeItem('aiSuggestionApplied');
          localStorage.removeItem('aiSuggestionData');
          localStorage.removeItem('aiSuggestionTimestamp');
          
          // Show success toast
          setTimeout(() => {
            toast({
              title: "AI Campaign Applied!",
              description: `Applied "${suggestion.campaignName}" with ${suggestion.suggestedLocations?.length || 0} locations.`,
            });
          }, 500); // Delay to ensure component is fully loaded
          
        } catch (error) {
          devError('Error parsing AI suggestion data:', error);
          localStorage.removeItem('aiSuggestionData');
          localStorage.removeItem('aiSuggestionApplied');
        }
      } else {
        // Load regular localStorage data if no AI suggestion
        if (savedStep) setCurrentStep(parseInt(savedStep, 10) || 0);
        if (savedName) {
          setCampaignName(savedName);
        }
        if (savedFormData) {
          try {
            setFormData(JSON.parse(savedFormData));
          } catch (e) {
            devError('Error parsing form data:', e);
          }
        }
        if (savedLocations) {
          try {
            setSelectedLocations(JSON.parse(savedLocations));
          } catch (e) {
            devError('Error parsing locations:', e);
          }
        }
        if (savedDuration) setDuration(parseInt(savedDuration, 10) || 14);
        if (savedDevices) setDevices(parseInt(savedDevices, 10) || 0);
      }
    };

    loadDraftData();
  }, [draftId, toast]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [mode, campaignId, draftId, toast]);

  // Post-mount AI suggestion applier (handles suggestions applied without reload)
  useEffect(() => {
    if (isLoading) return;
    if (aiSuggestionHandledRef.current) return;

    const aiSuggestionApplied = localStorage.getItem('aiSuggestionApplied');
    const aiSuggestionData = localStorage.getItem('aiSuggestionData');

    if (aiSuggestionApplied === 'true' && aiSuggestionData) {
      try {
        const suggestion = JSON.parse(aiSuggestionData);
        console.log('🧠 Detected AI suggestion post-mount, applying now:', suggestion);

        if (suggestion.campaignName) setCampaignName(suggestion.campaignName);
        if (suggestion.duration) setDuration(suggestion.duration);

        if (suggestion.companyName || suggestion.brandName || suggestion.personInCharge) {
          setFormData({
            companyName: suggestion.companyName || '',
            brandName: suggestion.brandName || '',
            personInCharge: suggestion.personInCharge || '',
            jobTitle: suggestion.jobTitle || '',
            email: suggestion.email || '',
            mobile: suggestion.mobile || ''
          });
        }

        if (Array.isArray(suggestion.suggestedLocations) && suggestion.suggestedLocations.length > 0) {
          const selectedLocationObjects = resolveSuggestedLocations(suggestion.suggestedLocations, datasetLocations as any);
          setSelectedLocations(selectedLocationObjects as any);
          const totalDevices = selectedLocationObjects.reduce((sum: number, loc: any) => sum + (loc.devices || 0), 0);
          setDevices(totalDevices);
          
          // Also store in localStorage for persistence
          localStorage.setItem('selectedLocations', JSON.stringify(selectedLocationObjects));
          localStorage.setItem('devices', totalDevices.toString());
        }

        // Apply dates based on duration
        if (suggestion.duration) {
          const today = new Date();
          const minAllowedDate = new Date(today);
          minAllowedDate.setDate(today.getDate() + 3);
          minAllowedDate.setHours(0, 0, 0, 0);
          
          const startDate = new Date(minAllowedDate);
          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + suggestion.duration - 1);
          
          DateStorageService.saveDate(startDate, 'start');
          DateStorageService.saveDate(endDate, 'end');
          DateStorageService.saveDuration(suggestion.duration);
        }

        // Clear suggestion data after applying
        localStorage.removeItem('aiSuggestionApplied');
        localStorage.removeItem('aiSuggestionData');

        aiSuggestionHandledRef.current = true;
        console.log('✅ AI suggestion applied post-mount');
      } catch (e) {
        console.error('Failed to apply AI suggestion post-mount:', e);
      }
    }
  }, [isLoading, setSelectedLocations]);
  // Handle form input changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(`Form field ${name} changed to: "${value}"`);
    
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Step navigation
  const handleNext = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  }, []);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleStepClick = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  // Data setters with enhanced logging
  const setCampaignNameWithSync = useCallback((name: string) => {
    console.log(`Campaign name changed to: "${name}"`);
    setCampaignName(name);
  }, []);

  const setSelectedLocationsWithSync = useCallback((locations: Location[]) => {
    console.log('🎯 LOCATIONS: Setting locations, count:', locations.length);
    console.log('🎯 LOCATIONS: Location details:', locations.map(loc => ({ name: loc.name, devices: loc.devices })));
    
    setSelectedLocations(locations);
    
    // Update devices count
    const totalDevices = locations.reduce((sum, loc) => sum + (loc.devices || 0), 0);
    console.log('🎯 LOCATIONS: Total devices calculated:', totalDevices);
    setDevices(totalDevices);
    
    // Force trigger auto-save immediately for location changes
    console.log('🎯 LOCATIONS: Forcing immediate auto-save trigger...');
    setTimeout(() => {
      triggerAutoSave();
    }, 100);
  }, [triggerAutoSave]);

  const setDurationWithSync = useCallback((newDuration: number) => {
    console.log(`Duration changed to: ${newDuration}`);
    setDuration(newDuration);
  }, []);

  return {
    isLoading,
    currentStep,
    campaignName,
    formData,
    selectedLocations,
    duration,
    devices,
    autoSaveStatus,
    lastSaveTime,
    validationErrors,
    // Creative upload functionality (now integrated)
    creatives,
    locationCreatives,
    mediaOwnerCreatives,
    isCreativeUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    removeCreative,
    handleAreaClick,
    handleLocationFileChange,
    handleMediaOwnerFileChange,
    removeLocationCreative,
    removeMediaOwnerCreative,
    handleUrlSubmit,
    handleLocationUrlSubmit,
    handleMediaOwnerUrlSubmit,
    updateLocationCreativeDays,
    updateMediaOwnerCreativeDays,
    useLocationSpecificCreatives,
    useMediaOwnerSpecificCreatives,
    // Navigation and handlers
    handleNext,
    handleBack,
    handleStepClick,
    handleInputChange,
    setCampaignName: setCampaignNameWithSync,
    setSelectedLocations: setSelectedLocationsWithSync,
    setDuration: setDurationWithSync,
    setUseLocationSpecificCreatives,
    setUseMediaOwnerSpecificCreatives
  };
};
